import { CloseOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import {
	Button,
	Dropdown,
	Empty,
	Input,
	Popover,
	Select,
	Space,
	Tooltip,
	Typography,
} from "antd";
import type { MenuProps } from "antd";
import { useMemo, useState, useRef } from "react";
import "./SmartFilter.scss";

const { Text } = Typography;

export interface FilterCriteria {
	id: string;
	column: string;
	columnLabel: string;
	operator: "equals" | "contains" | "in";
	value: string | string[];
	displayValue: string;
}

export interface FilterableColumn {
	key: string;
	label: string;
	dataIndex: string | string[];
	type: "text" | "number" | "select";
}

interface SmartFilterProps {
	columns: FilterableColumn[];
	filters: FilterCriteria[];
	onFiltersChange: (filters: FilterCriteria[]) => void;
	data: any[]; // Used to get unique values for dropdown options
	loading?: boolean;
	// New props for integrated search
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	showIntegratedSearch?: boolean;
}

const generateFilterId = () =>
	`filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getNestedValue = (obj: any, path: string | string[]): any => {
	if (typeof path === "string") {
		return obj?.[path];
	}
	return path.reduce((acc, key) => acc?.[key], obj);
};

export const SmartFilter = ({
	columns,
	filters,
	onFiltersChange,
	data,
	loading = false,
	searchValue: externalSearchValue,
	onSearchChange,
	showIntegratedSearch = false,
}: SmartFilterProps) => {
	const [isAddingFilter, setIsAddingFilter] = useState(false);
	const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
	const [selectedValues, setSelectedValues] = useState<string[]>([]);
	const [filterSearchValue, setFilterSearchValue] = useState("");
	const [editingFilterId, setEditingFilterId] = useState<string | null>(null);
	const [showFilterDropdown, setShowFilterDropdown] = useState(false);
	const [localSearchValue, setLocalSearchValue] = useState(externalSearchValue || "");
	const searchInputRef = useRef<any>(null);

	// Get unique values for the selected column from data
	const columnOptions = useMemo(() => {
		if (!selectedColumn) return [];

		const column = columns.find((c) => c.key === selectedColumn);
		if (!column) return [];

		const valueSet = new Set<string>();
		data.forEach((item) => {
			const value = getNestedValue(item, column.dataIndex);
			if (value != null && value !== "") {
				valueSet.add(String(value));
			}
		});

		const sortedValues = Array.from(valueSet).sort((a, b) =>
			a.toLowerCase().localeCompare(b.toLowerCase()),
		);

		// Filter by search value
		if (filterSearchValue) {
			return sortedValues.filter((v) =>
				v.toLowerCase().includes(filterSearchValue.toLowerCase()),
			);
		}

		return sortedValues;
	}, [selectedColumn, data, columns, filterSearchValue]);

	// Reset form when closing
	const resetForm = () => {
		setSelectedColumn(null);
		setSelectedValues([]);
		setFilterSearchValue("");
		setIsAddingFilter(false);
		setEditingFilterId(null);
	};

	// Handle column selection from dropdown
	const handleColumnSelect = (columnKey: string) => {
		setSelectedColumn(columnKey);
		setSelectedValues([]);
		setFilterSearchValue("");
		setEditingFilterId(null);
		setIsAddingFilter(true);
		setShowFilterDropdown(false);
	};

	// Handle search submit
	const handleSearchSubmit = () => {
		if (onSearchChange) {
			onSearchChange(localSearchValue);
		}
		setShowFilterDropdown(false);
	};

	// Find suggested filters based on current search input
	const suggestedFilters = useMemo(() => {
		if (!localSearchValue || localSearchValue.trim().length < 2) return [];

		const searchLower = localSearchValue.toLowerCase().trim();
		const suggestions: Array<{
			column: FilterableColumn;
			value: string;
			matchType: "exact" | "startsWith" | "contains";
		}> = [];

		// Check each column for matching values
		for (const column of columns) {
			const valueSet = new Set<string>();
			data.forEach((item) => {
				const value = getNestedValue(item, column.dataIndex);
				if (value != null && value !== "") {
					valueSet.add(String(value));
				}
			});

			// Find matches
			for (const value of valueSet) {
				const valueLower = value.toLowerCase();
				if (valueLower === searchLower) {
					suggestions.push({ column, value, matchType: "exact" });
				} else if (valueLower.startsWith(searchLower)) {
					suggestions.push({ column, value, matchType: "startsWith" });
				} else if (valueLower.includes(searchLower) && suggestions.length < 8) {
					suggestions.push({ column, value, matchType: "contains" });
				}
			}
		}

		// Sort by match quality and limit results
		return suggestions
			.sort((a, b) => {
				const order = { exact: 0, startsWith: 1, contains: 2 };
				return order[a.matchType] - order[b.matchType];
			})
			.slice(0, 5);
	}, [localSearchValue, columns, data]);

	// Handle applying a suggested filter
	const handleApplySuggestedFilter = (column: FilterableColumn, value: string) => {
		const newFilter: FilterCriteria = {
			id: generateFilterId(),
			column: column.key,
			columnLabel: column.label,
			operator: "equals",
			value: value,
			displayValue: value,
		};
		onFiltersChange([...filters, newFilter]);
		setLocalSearchValue("");
		setShowFilterDropdown(false);
	};

	// Dropdown menu items for filter columns
	const filterDropdownItems: MenuProps["items"] = [
		// Show suggested filters first if there are matches
		...(suggestedFilters.length > 0
			? [
					{
						key: "suggestions-header",
						type: "group" as const,
						label: (
							<span style={{ color: "#1890ff", fontSize: 12, fontWeight: 500 }}>
								<FilterOutlined style={{ marginRight: 6 }} />
								Suggested filters
							</span>
						),
					},
					...suggestedFilters.map((suggestion, index) => ({
						key: `suggestion-${index}`,
						label: (
							<span>
								<span style={{ color: "#8c8c8c" }}>{suggestion.column.label}:</span>{" "}
								<span style={{ fontWeight: 500 }}>{suggestion.value}</span>
							</span>
						),
						onClick: () =>
							handleApplySuggestedFilter(suggestion.column, suggestion.value),
					})),
					{ type: "divider" as const, key: "divider" },
			  ]
			: []),
		{
			key: "header",
			type: "group",
			label: (
				<span style={{ color: "#8c8c8c", fontSize: 12, fontWeight: 500 }}>
					<FilterOutlined style={{ marginRight: 6 }} />
					Add filter by column
				</span>
			),
		},
		...columns.map((col) => ({
			key: col.key,
			label: col.label,
			onClick: () => handleColumnSelect(col.key),
		})),
	];

	// Handle adding or updating a filter
	const handleApplyFilter = () => {
		if (!selectedColumn || selectedValues.length === 0) return;

		const column = columns.find((c) => c.key === selectedColumn);
		if (!column) return;

		const displayValue =
			selectedValues.length === 1
				? selectedValues[0]
				: `${selectedValues.length} selected`;

		const newFilter: FilterCriteria = {
			id: editingFilterId || generateFilterId(),
			column: selectedColumn,
			columnLabel: column.label,
			operator: selectedValues.length > 1 ? "in" : "equals",
			value: selectedValues.length > 1 ? selectedValues : selectedValues[0],
			displayValue,
		};

		if (editingFilterId) {
			// Update existing filter
			onFiltersChange(
				filters.map((f) => (f.id === editingFilterId ? newFilter : f)),
			);
		} else {
			// Add new filter
			onFiltersChange([...filters, newFilter]);
		}

		resetForm();
	};

	// Handle removing a filter
	const handleRemoveFilter = (filterId: string) => {
		onFiltersChange(filters.filter((f) => f.id !== filterId));
	};

	// Handle editing a filter
	const handleEditFilter = (filter: FilterCriteria) => {
		setSelectedColumn(filter.column);
		setSelectedValues(
			Array.isArray(filter.value) ? filter.value : [filter.value],
		);
		setEditingFilterId(filter.id);
		setIsAddingFilter(true);
	};

	// Handle clearing all filters
	const handleClearAll = () => {
		onFiltersChange([]);
		resetForm();
	};

	// Column selector content
	const columnSelectorContent = (
		<div className="smart-filter-column-selector">
			<Text strong style={{ display: "block", marginBottom: 8 }}>
				Select Column
			</Text>
			<Select
				placeholder="Choose a column to filter..."
				style={{ width: "100%" }}
				value={selectedColumn}
				onChange={(value) => {
					setSelectedColumn(value);
					setSelectedValues([]);
					setFilterSearchValue("");
				}}
				options={columns.map((col) => ({
					label: col.label,
					value: col.key,
				}))}
			/>

			{selectedColumn && (
				<>
					<Text
						strong
						style={{ display: "block", marginTop: 16, marginBottom: 8 }}
					>
						Select Values
					</Text>
					<Input.Search
						placeholder="Search values..."
						value={filterSearchValue}
						onChange={(e) => setFilterSearchValue(e.target.value)}
						style={{ marginBottom: 8 }}
						allowClear
					/>
					<div className="smart-filter-values-list">
						{columnOptions.length === 0 ? (
							<Empty
								description="No values found"
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						) : (
							<Select
								mode="multiple"
								placeholder="Select one or more values..."
								style={{ width: "100%" }}
								value={selectedValues}
								onChange={setSelectedValues}
								maxTagCount={3}
								maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
								options={columnOptions.map((value) => ({
									label: value,
									value: value,
								}))}
								filterOption={false} // We're already filtering via searchValue
								showSearch={false}
								popupMatchSelectWidth={true}
								listHeight={150}
								placement="topLeft"
							/>
						)}
					</div>
					<div
						style={{
							marginTop: 16,
							paddingTop: 8,
							borderTop: "1px solid #f0f0f0",
							display: "flex",
							justifyContent: "flex-end",
							gap: 8,
						}}
					>
						<Button onClick={resetForm}>Cancel</Button>
						<Button
							type="primary"
							onClick={handleApplyFilter}
							disabled={selectedValues.length === 0}
						>
							{editingFilterId ? "Update Filter" : "Apply Filter"}
						</Button>
					</div>
				</>
			)}
		</div>
	);

	// Render the filter chips
	const renderFilterChips = () => (
		<>
			{filters.map((filter) => (
				<Popover
					key={filter.id}
					content={columnSelectorContent}
					title={null}
					trigger="click"
					open={isAddingFilter && editingFilterId === filter.id}
					onOpenChange={(open) => {
						if (open) {
							handleEditFilter(filter);
						} else {
							resetForm();
						}
					}}
					placement="bottomLeft"
					overlayStyle={{ width: 320 }}
				>
					<div className="smart-filter-chip" onClick={() => handleEditFilter(filter)}>
						<CloseOutlined
							className="filter-chip-close"
							onClick={(e) => {
								e.stopPropagation();
								handleRemoveFilter(filter.id);
							}}
						/>
						<span className="filter-chip-column">{filter.columnLabel}</span>
						<Tooltip
							title={
								Array.isArray(filter.value)
									? filter.value.join(", ")
									: filter.value
							}
						>
							<span className="filter-chip-value">{filter.displayValue}</span>
						</Tooltip>
					</div>
				</Popover>
			))}
		</>
	);

	// Render the add filter button
	const renderAddFilterButton = () => (
		<Popover
			content={columnSelectorContent}
			title={null}
			trigger="click"
			open={isAddingFilter && !editingFilterId}
			onOpenChange={(open) => {
				if (!open) resetForm();
				else {
					setEditingFilterId(null);
					setIsAddingFilter(open);
				}
			}}
			placement="bottomLeft"
			overlayStyle={{ width: 320 }}
		>
			<div
				className="smart-filter-add-btn"
				style={{ display: loading ? "none" : "flex" }}
			>
				<FilterOutlined />
				<span>Add filter</span>
			</div>
		</Popover>
	);

	// Integrated search mode - search input with filter dropdown
	if (showIntegratedSearch) {
		return (
			<div className="smart-filter-container smart-filter-integrated">
				<Space wrap size={[8, 8]} align="center">
					{/* Search with filter dropdown */}
					<Dropdown
						menu={{ items: filterDropdownItems }}
						open={showFilterDropdown && !isAddingFilter}
						onOpenChange={(open) => {
							// Only update if we're not in the middle of adding a filter
							if (!isAddingFilter) {
								setShowFilterDropdown(open);
							}
						}}
						trigger={["click"]}
						placement="bottomLeft"
					>
						<Input
							ref={searchInputRef}
							placeholder="Search or add filter..."
							prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
							suffix={
								<FilterOutlined
									style={{ color: "#bfbfbf", cursor: "pointer" }}
									onClick={(e) => {
										e.stopPropagation();
										setShowFilterDropdown(!showFilterDropdown);
									}}
								/>
							}
							style={{ width: 300 }}
							value={localSearchValue}
							onChange={(e) => {
								const newValue = e.target.value;
								setLocalSearchValue(newValue);
								// Show dropdown when typing to display suggestions
								if (newValue.length >= 2) {
									setShowFilterDropdown(true);
								}
								// If cleared (empty), trigger search update to remove filter
								if (newValue === "" && onSearchChange) {
									onSearchChange("");
								}
							}}
							onPressEnter={handleSearchSubmit}
							allowClear
							disabled={loading}
						/>
					</Dropdown>

					{/* Popover for column value selection (appears when a column is selected) */}
					<Popover
						content={columnSelectorContent}
						title={null}
						trigger="click"
						open={isAddingFilter && !editingFilterId && selectedColumn !== null}
						onOpenChange={(open) => {
							if (!open) resetForm();
						}}
						placement="bottomLeft"
						overlayStyle={{ width: 320 }}
					>
						<span /> {/* Hidden anchor for popover */}
					</Popover>

					{/* Filter Chips */}
					{renderFilterChips()}

					{/* Clear All Button */}
					{filters.length > 1 && (
						<Button
							type="link"
							size="small"
							onClick={handleClearAll}
							style={{ color: "#8c8c8c", padding: "0 4px" }}
						>
							Clear all
						</Button>
					)}
				</Space>
			</div>
		);
	}

	// Standard mode - separate search and filter
	return (
		<div className="smart-filter-container">
			<Space wrap size={[8, 8]} align="center">
				{/* Filter Chips/Tags */}
				{renderFilterChips()}

				{/* Add Filter Button - subtle style */}
				{renderAddFilterButton()}

				{/* Clear All Button */}
				{filters.length > 1 && (
					<Button
						type="link"
						size="small"
						onClick={handleClearAll}
						style={{ color: "#8c8c8c", padding: "0 4px" }}
					>
						Clear all
					</Button>
				)}
			</Space>
		</div>
	);
};

// Default filterable columns for locations
export const LOCATION_FILTERABLE_COLUMNS: FilterableColumn[] = [
	{ key: "city", label: "City", dataIndex: "city", type: "text" },
	{ key: "state", label: "State", dataIndex: "state", type: "text" },
	{ key: "zip", label: "Zip", dataIndex: "zip", type: "text" },
	{
		key: "store_number",
		label: "Store Number",
		dataIndex: "store_number",
		type: "text",
	},
];

// Cushman-specific columns
export const CUSHMAN_FILTERABLE_COLUMNS: FilterableColumn[] = [
	{ key: "name", label: "Site Name", dataIndex: "name", type: "text" },
	{ key: "city", label: "City", dataIndex: "city", type: "text" },
	{ key: "state", label: "State", dataIndex: "state", type: "text" },
	{ key: "zip", label: "Zip", dataIndex: "zip", type: "text" },
	{
		key: "store_number",
		label: "Store Number",
		dataIndex: "store_number",
		type: "text",
	},
];
